import { PlatformType, ProviderType } from "@saysimple-platform/dtos";
import { encode } from "html-entities";
import linkifyHtml from "linkify-html";
import * as linkify from "linkifyjs";
import * as lodash from "lodash";

export default function messageParser(message: string, channel: { platform?: PlatformType; provider?: ProviderType }) {
    if (channel.platform === "test" || channel.platform === "whatsapp") {
        return parseForWhatsapp(message);
    }

    // Very important function, disableling this will result in XSS in our platform
    message = encode(message)
        // &apos; is not recognized by linkifyHtml as a valid escaped character so replace it with it hex value to avoid this getting double encoded
        .replaceAll("&apos;", "&#39;");

    message = handleEmojies(message);

    // Find which parse function to apply
    if (channel.platform === "web" && channel.provider === "smooch") {
        return linkifyHtml(parseForSmoochWeb(message));
    }

    // No parsing needed
    return linkifyHtml(message);
}

// Just copy-pasted from frontend v2, don't know what it should do exactly...
const handleEmojies = (message: string): string => {
    const noEmojiRegex =
        /[^\u00a9|\u00ae|\u2000-\u3300|\ud83c|\ud000-\udfff|\ud83d|\ud000-\udfff|\ud83e|\ud000-\udfff|\s|\uFE0F|(&zwj;)]/;
    const emojiRegex =
        /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])+/g;

    if (!message.match(noEmojiRegex) && message.length <= 20) {
        //this.allEmojies = true;
    }

    return message.replace(emojiRegex, (emoji) => `<span class="emoji">${emoji}</span>`);
};

// @see https://docs.smooch.io/guide/shorthand/#shorthand
// @NOTE copy-pasted from frontend v1: "@/mixins/messageParserMixin.js"
const parseForSmoochWeb = (message: string): string => {
    const buttons = message.match(/%\[.*]\(\S*\)/g) || [];
    const images = message.match(/!\[.*]\(\S*\)/g) || [];
    const image = images[0]; // Only one can be displayed at a time
    const buttonTypes = buttons.map((button) => button.match(/\(.*\)/) || "()").flat(1); // Array with strings like '(reply:APPLES)', '(postback:whatever)', '(http://some.url)'

    // NOTE: Smooch will only display a button as a reply button if all letters in the reply keyword are lower cased (2021-7-2, Matthijs)
    // I don't agree with that decision but we'll do the same for styling here, because the goal is to keep the styling consistent
    if (buttonTypes.length > 0 && buttonTypes.every((type) => /\(reply:/.test(type))) {
        // Filter out what the text on the buttons needs to be, we don't actually need the rest because agents can't click them anyway
        const buttonTexts = buttons.map((button) => {
            const truncatedText = button.replace(/\(.*\)/, "");
            return truncatedText.substring(2, truncatedText.length - 1);
        });
        // Remove the shorthand code from the message
        message = message.replace(/%\[.*]\(\S*\)/g, "");
        if (message) {
            message += "\n";
        }
        // Add the button html code (buttons are always placed at the end)
        buttonTexts.forEach((text) => (message += `<br /><button class="reply-button smooch">${text}</button>`));
    } else if (buttons.length > 0) {
        // We can use indices to match with buttonTypes array
        const buttonTexts = buttons.map((button) => {
            const truncatedText = button.replace(/\(.*\)/, "");
            return truncatedText.substring(2, truncatedText.length - 1);
        });
        // Remove the shorthand code from the message
        message = message.replace(/%\[.*]\(\S*\)/g, "");
        if (message) {
            message += "\n";
        }
        // Add the buttons as html (always at the end)
        for (let i = 0; i < buttonTexts.length; i++) {
            // if button has a link as a payload create a clickable button (a) that links to the url.
            message += buttonTypes[i].match(/\(http/)
                ? `<br /><a href="${buttonTypes[i].substring(
                      1,
                      buttonTypes[i].length - 1
                  )}" class="image-button smooch" target="_blank">${buttonTexts[i]}</a>`
                : `<br /><button class="${
                      buttonTypes[i].match(/\(location\)/) ? "location" : "generic-button"
                  } smooch">${buttonTexts[i]}</button>`;
        }
    }

    if (!image) {
        return message;
    }
    // Remove the image
    message = message.replace(image, "");
    // Get the image url
    const imageLocation = image.match(/\(.*\)/)?.[0];

    if (!imageLocation) {
        return message;
    }

    const url = imageLocation.substring(1, imageLocation.length - 1);
    // Add the image as html (images always on top)
    message = `<img class="image smooch" src="${url}" alt="${url}" /><br/>${message}`;

    // Replace all other images with just their link
    for (let i = 1; i < images.length; i++) {
        const actionBlock = images[i].match(/\(.*\)/)?.[0] || "()";
        const url = actionBlock.substring(1, actionBlock.length - 1);
        message = message.replace(images[i], url);
    }

    return message;
};

const parseForWhatsapp = (message: string): string => {
    // Before anything, to prevent rendering errors need to convert less-than and greater-than signs manually
    message = message.replaceAll("<", "&lt;").replaceAll(">", "&gt;");

    // Linkify BEFORE parsing the styling, because e-mail links break if
    // containing underscores or tildes if we linkify afterward
    const links = linkify.find(message, {
        defaultProtocol: "https",
    });

    // Encode links manualy to fix parser issue
    links.forEach((link) => {
        // change all possible parsed characters to their html counterpart to make sure they are not parsed
        const text = link.value
            .replaceAll("_", "&lowbar;")
            .replaceAll("~", "&tilde;")
            .replaceAll("*", "&ast;")
            .replaceAll("`", "&grave;")
            .replaceAll(">", "&gt;");

        // Encode Everything from the URI component to avoid XSS
        const href = encodeURIComponent(link.href)
            .replaceAll("_", "%5f")
            .replaceAll("~", "%7e")
            .replaceAll("*", "%2a")
            // Enable important url characters to make sure urls work still fine
            .replaceAll("%2F", "/")
            .replaceAll("%3A", ":")
            .replaceAll("%3F", "?")
            .replaceAll("%23", "#")
            .replaceAll("%26", "&")
            .replaceAll("%3B", ";")
            .replaceAll("%25", "%")
            .replaceAll("%40", "@")
            .replaceAll("%3D", "=");

        const escapedValue = lodash.escapeRegExp(link.value);
        const escapedHref = lodash.escapeRegExp(href);

        // Search for value instance that is currently not in an url
        const searchValue = new RegExp(
            `(?<!(<a href="${escapedHref}" target="_blank">)|(<a href="[^"]+))(${escapedValue})(?!" target="_blank">)`
        );

        message = message.replace(searchValue, `<a href="${href}" target="_blank">${text}</a>`);
    });
    const blockQuoteRegex = /(?<text>(^&gt; .*\n?)+)/gm;
    const boldRegex = /\*(?<text>[^*]+)\*/g;
    const italicRegex = /_(?<text>[^_]+)_/g;
    const strikeRegex = /~(?<text>[^~]+)~/g;
    const inlineCodeRegex = /`(?<text>.+?)`/g;
    const monoRegex = /`{3}(?<text>.+?)`{3}/g;

    message = message
        .replace(blockQuoteRegex, (match) => {
            // we add the | in front of each lines who is block quoted
            const text = match.replace(/^&gt;\s/gm, "| ");
            // here  we create the html section of the blockquote
            return `<span class="formatting-section">${text.trim()}</span>\n`;
        })
        .replace(boldRegex, "<b>$<text></b>")
        .replace(italicRegex, "<i>$<text></i>")
        .replace(strikeRegex, "<s>$<text></s>")
        .replace(inlineCodeRegex, `<span class="formatting-section inline-code-section text-monospace">$<text></span>`)
        .replace(monoRegex, `<span class="text-monospace">$<text></span>`);
    // Handle emojis last
    return handleEmojies(message);
};
